// @TODO あとでselectBoxに修正する

import { useMismatchWarningRules } from '@/components/ReportForm/hooks/useMismatchWarningRules';
import { useTranslation } from '@/i18n';
import {
  FormInput,
  FormInputProps,
} from '@moneyforward/ap-frontend-components';
import { ReactNode, memo, useMemo } from 'react';
import { FieldValues, RegisterOptions } from 'react-hook-form';
import { InstantPayeeInputBase } from '../InstantPayeeInputBase/InstantPayeeInputBase';

const ACCOUNT_NUMBER_DIGIT_PATTERN = /^[0-9]{7}$/;

// warningOrError is not part of RegisterOptions's public type, hence the
// assertion below. Named so the assertion stays a single line and isn't
// shifted onto a different line by prettier reflowing the inline object.
type MismatchWarningOrError = {
  warningOrError: {
    required: (value: unknown) => unknown;
    mismatch: (value: unknown) => unknown;
  };
};

export type Props<TFieldValues extends FieldValues> = {
  required?: boolean;
  className?: string;
  caption?: ReactNode;
  tooltip?: ReactNode;
  mismatchMessage?: string;
} & FormInputProps<TFieldValues>;

const InnerInstantPayeeBankNumberForm = <TFieldValues extends FieldValues>({
  control,
  name,
  inputId,
  required = false,
  mismatchMessage,
  ...rest
}: Props<TFieldValues>) => {
  const { t } = useTranslation();
  const { rules: mismatchRules, captionRender } = useMismatchWarningRules(
    name,
    required,
    t('required'),
    mismatchMessage,
    undefined
  );

  const rules = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const { warningOrError } = mismatchRules as MismatchWarningOrError;

    // warningOrError is not part of RegisterOptions, so the result needs an
    // assertion. Built-in pattern/maxLength skip it, so the checks stay here.
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return {
      warningOrError: {
        required: warningOrError.required,
        accountNumberDigits: (value: unknown) => {
          if (value === undefined || value === null || value === '') {
            return true;
          }
          const text = String(value);
          if (!ACCOUNT_NUMBER_DIGIT_PATTERN.test(text)) {
            return {
              error: true,
              message: t('payee_account_number_digit_error'),
            };
          }
          return true;
        },
        mismatch: warningOrError.mismatch,
      },
    } as RegisterOptions<TFieldValues>;
  }, [mismatchRules, t]);
  const inputProps = useMemo(
    () => ({
      ...rest.inputProps,
      placeholder: t('payee_account_number'),
    }),
    [rest.inputProps, t]
  );
  const labelProps = useMemo(() => ({ children: '口座番号' }), []);

  return (
    <InstantPayeeInputBase labelProps={labelProps}>
      <FormInput<TFieldValues>
        inputId={inputId}
        name={name}
        control={control}
        inputProps={inputProps}
        {...rest}
        rules={rules}
        captionRender={captionRender}
      />
    </InstantPayeeInputBase>
  );
};

InnerInstantPayeeBankNumberForm.displayName = 'InnerInstantPayeeBankNumberForm';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const InstantPayeeBankNumberForm = memo((props) => (
  <InnerInstantPayeeBankNumberForm {...props} />
)) as typeof InnerInstantPayeeBankNumberForm;
InstantPayeeBankNumberForm.displayName = 'InstantPayeeBankNumberForm';
